import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import { useIntercom } from 'react-use-intercom'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'

interface IChatContext {
  isFrontChatBooted: boolean
  boot: (frontChatInitParams?: IFrontChatInitParams, isFrontChatBooted?: boolean) => void
  update: (userDetails?: IChatIdentityParams | null) => void
  show: () => void
  hide: () => void
  shutdown: (clearSession?: boolean) => void
}

const chatWidgetDefaultState: IChatContext = {
  isFrontChatBooted: false,
  boot: () => {},
  update: () => {},
  show: () => {},
  hide: () => {},
  shutdown: () => {},
}

export const ChatWidgetContext = createContext<IChatContext>(chatWidgetDefaultState)

/**
 * @see https://dev.frontapp.com/docs/chat-sdk-reference#options-you-can-pass-through-init
 */
interface IFrontChatInitParams extends IChatIdentityParams {
  useDefaultLauncher?: boolean
  shouldShowWindowOnLaunch?: boolean
  shouldHideCloseButton?: boolean
  isMobileWebview?: boolean
  onInitCompleted?: Function
}

interface IChatIdentityParams {
  email?: string
  name?: string
  /**
   * SHA-256 hash against Front channel secret. Generated in marketplace
   */
  userHash?: string | null
  /**
   * @deprecated Required for Intercom chat
   */
  userId?: string
}

interface IChatWidgetProviderProps {
  /**
   * Channel ID for the associated Front message inbox
   */
  chatId: string
  children: React.ReactNode
}

/**
 * Provides access to the Intercom or Front Chat APIs
 * @see https://github.com/devrnt/react-use-intercom
 * @see https://dev.frontapp.com/docs/chat-sdk-reference
 */
export function ChatWidgetProvider({ chatId: frontChatId, children }: IChatWidgetProviderProps) {
  const isFrontChatEnabled = useFeatureFlag('fmsFrontChat')
  const [_isFrontBooted, setFrontBooted] = useState<boolean>(false)
  // If Front is disabled, we defer to `react-use-intercom` to confirm the Intercom state
  const isFrontBooted = isFrontChatEnabled ? _isFrontBooted : true

  const bootFrontChat = () => {
    setFrontBooted(true)
  }
  const shutdownFrontChat = () => {
    setFrontBooted(false)
  }

  const {
    boot: bootIntercom,
    hide: hideIntercom,
    show: showIntercom,
    shutdown: shutdownIntercom,
    update: updateIntercom,
  } = useIntercom()

  /**
   * Checks that the Front widget exists in the DOM
   */
  const ensureFront = (apiMethod: string, params = {}) => {
    if ((window as any)?.FrontChat) {
      FrontChat(apiMethod, params)
    }
  }

  /**
   * Initializes the Front or Intercom chat widgets and mounts them on the screen
   * @param frontChatInitParams https://dev.frontapp.com/docs/chat-sdk-reference#frontchatinit-options
   */
  const boot = useCallback(
    (
      frontChatInitParams: IFrontChatInitParams = {
        useDefaultLauncher: true,
      },
      isFrontChatBooted: boolean = isFrontBooted
    ) => {
      if (isFrontChatEnabled) {
        // additional check to prevent remounting the widget and flickering
        if (!isFrontChatBooted) {
          ensureFront('init', {
            ...frontChatInitParams,
            chatId: frontChatId,
            onInitCompleted: bootFrontChat,
          })
        }
      } else if (__INTERCOM_APP_ID__) {
        bootIntercom({ hideDefaultLauncher: !frontChatInitParams.useDefaultLauncher })
      }
    },
    [bootIntercom, frontChatId, isFrontBooted, isFrontChatEnabled]
  )

  /**
   * Front - Provides the name and email of a user
   * Intercom - Provides name and email when present, otherwise refreshes widget
   * @param userDetails
   */
  const update = useCallback(
    (userDetails?: IChatIdentityParams | null) => {
      const { email, name, userHash, userId } = userDetails || {}
      if (isFrontChatEnabled) {
        if (userHash) {
          ensureFront('identity', { email, name, userHash })
        }
      } else if (__INTERCOM_APP_ID__) {
        updateIntercom({ email, name, userId })
      }
    },
    [isFrontChatEnabled, updateIntercom]
  )

  /**
   * "Opens" the relevant widget's messenger panel
   */
  const show = useCallback(() => {
    if (isFrontChatEnabled) {
      ensureFront('show')
    } else if (__INTERCOM_APP_ID__) {
      showIntercom()
    }
  }, [isFrontChatEnabled, showIntercom])

  /**
   * "Closes" the relevant widget's messenger panel
   */
  const hide = useCallback(() => {
    if (isFrontChatEnabled) {
      ensureFront('hide')
    } else if (__INTERCOM_APP_ID__) {
      hideIntercom()
    }
  }, [hideIntercom, isFrontChatEnabled])

  /**
   * Dismounts and removes the chat widget
   * @param clearSession Optional, ends current conversation
   */
  const shutdown = useCallback(
    (clearSession = false) => {
      if (isFrontChatEnabled) {
        ensureFront('shutdown', { clearSession })
        shutdownFrontChat()
      } else if (__INTERCOM_APP_ID__) {
        shutdownIntercom()
      }
    },
    [isFrontChatEnabled, shutdownIntercom]
  )

  const chatContextState = useMemo(
    () => ({
      boot,
      update,
      show,
      hide,
      shutdown,
      isFrontChatBooted: isFrontBooted,
    }),
    [boot, hide, isFrontBooted, show, shutdown, update]
  )

  return (
    <ChatWidgetContext.Provider value={chatContextState}>{children}</ChatWidgetContext.Provider>
  )
}

export const useChatWidgetContext = () => useContext(ChatWidgetContext)
